import Pager from "components/Pager";
import { DAY_IN_MILLISECONDS, IS_DEV, STORAGE_PROJECT_TASKS_TABLE_SORTING } from "globals/constants";
import { TASK_CATEGORIES } from "globals/constants/task";
import { api } from "services/api";
import { tasks as constants } from "store/constants";
import { actionFactory as feedActionFactory } from "store/factories/feed";
import { actionLoaderFactory } from "store/factories/fetchable";
import { taskListSelectors } from "store/selectors";
import { dispatch } from "store/store";
import { notifyError } from "utils";

import { tasksAdditionalDetails } from "./taskAdditionalDetails";


export const { setPaging, setSort: setSortAction } = feedActionFactory(constants);

export const allTasksFactory = actionLoaderFactory(constants, constants.ALL_TASKS);
export const awardTasksFactory = actionLoaderFactory(constants, constants.AWARD_TASKS);
export const proposalTasksFactory = actionLoaderFactory(constants, constants.PROPOSAL_TASKS);

export const setFilters = filters => dispatch({ type: constants.SET_FILTERS, filters });
export const clearTaskList = () => allTasksFactory.setData({ items: [], totalItems: 0 });

const onError = (error, storeError) => {
	const message = IS_DEV ? error.message : "Something went wrong";

	notifyError(message, error);
	storeError(message);
	clearTaskList();
};

/**
 * Retrieves the tasks for a specific project.
 *
 * @returns {Promise} - A promise that resolves to the response from the API call.
 */
export const getProjectTasks = async (id, { isRefresh } = { isRefresh: false }) => {
	const {
		sort,
		filters,
		paging: { pageNumber, pageLimit },
	} = taskListSelectors.content();

	// Calculate the page offset based on the page number
	const pageOffset = Pager.pageOffsetByPageNumber(pageNumber);

	const params = {
		start: pageOffset * pageLimit,
		size: pageLimit,
		sort: [ sort.column, "id" ].filter(Boolean).join(","),
		order: [ sort.direction.slice(0, -6), "asc" ].filter(Boolean).join(","), // strip off "ending" to "asc" or "desc" for Flowable
		includeTaskLocalVariables: true,
		includeProcessVariables: true,
		submissionId: id,
	};

	if (filters.status.length > 0) {
		params.filterByStatus = filters.status;
	}

	if (filters.type.length > 0) {
		params.type = filters.type.join(",");
	} else {
		params.type = Object.values(TASK_CATEGORIES).join(",");
	}

	// Call the API to retrieve the tasks
	return await allTasksFactory.createLoaderAction(
		() => api.tasks.get(params, { retries: 0 }),
		[
			async response => {
				// Transform the response data into the desired format
				const items = response.data.map(item => {
					let lastUpdatedBy = item.variables.lastUpdatedBy;
					const teamName = item.variables.teamName;
					// Remove the team name from the assignee. fix for old tasks
					const assigneeName = item.variables.assignee?.replace(teamName, "");

					return {
						task: item.name,
						assigneeId: item.assignee,
						teamId: item.variables.toTeam,
						teamName,
						category: item.category,
						createdOn: item.createTime,
						description: item.description,
						dueOn: item.dueDate,
						internalDueDate: item.variables.internalDueDate,
						id: item.id, // need to use 'id' for the 'link-to-milestone'
						taskId: item.id,
						owner: item.owner,
						tenantId: item.tenantId,
						applicationHostUrl: item.variables.applicationHostUrl,
						assignedFromName: item.variables.assignedFrom,
						assigneeName,
						to: assigneeName, // need to use 'to' for the 'link-to-milestone'
						completedBy: item.variables.completedBy,
						projectId: item.variables.projectId,
						submissionId: item.variables.submissionId,
						status: item.variables.status,
						url: item.variables.url,
						lastUpdated: item.variables.lastUpdated,
						lastUpdatedBy,
						taskCounter: item.variables.taskCounter,
						taskCounterNumeric: item.variables.taskCounterNumeric,
						userCategory: item.variables.userCategory,
						userCategoryId: item.variables.userCategoryId,
						assigneeUserObject: {},
						ownerUserObject: {},
						milestoneId: item.variables.milestoneId,
						milestoneCounter: item.variables.milestoneCounter,
						percentComplete: item.percentComplete,
					};
				});

				// Update the tasks store with the retrieved data
				allTasksFactory.setData({
					items,
					totalItems: response.total,
					paging: { pageLimit, pageNumber },
				});

				tasksAdditionalDetails.asyncUpdateTasksInStore(tasksAdditionalDetails.getUserAdditionalDetails);

				return response;
			},
			error => onError(error, allTasksFactory.setError),
		],
		{ isRefresh },
	);
};

export const getAwardTasks = id => {
	return awardTasksFactory.createLoaderAction(
		() => api.tasks.getAwardTasks({ id }, { retries: 0 }),
		[
			response => awardTasksFactory.setData(response),
			error => onError(error, awardTasksFactory.setError),
		],
	);
};

export const getProposalTasks = id => {
	return proposalTasksFactory.createLoaderAction(
		() => api.tasks.getProposalTasks({ id }, { retries: 0 }),
		[
			response => proposalTasksFactory.setData(response),
			error => onError(error, proposalTasksFactory.setError),
		],
	);
};

const storeProjectTaskTableSorting = (column, direction) => {
	const content = {
		column,
		direction,
		expires: new Date().getTime() + DAY_IN_MILLISECONDS,
	};

	localStorage.setItem(
		STORAGE_PROJECT_TASKS_TABLE_SORTING,
		JSON.stringify(content),
	);
};

export const setSort = (column, direction) => {
	storeProjectTaskTableSorting(column, direction);
	setSortAction(column, direction);
};

export async function loadMilestoneOptions ({ searchText, submissionId }) {
	const params = {
		includeTaskLocalVariables: true,
		includeProcessVariables: true,
		sort: "taskCounterNumeric",
		order: "asc",
		start: 0,
		size: 30,
		type: TASK_CATEGORIES.CAYUSE_SP_MILESTONE,
		filterByStatus: "OPEN,ON_HOLD,IN_PROGRESS",
		searchText,
		submissionId,
		searchBy: "id,name",
	};

	try {
		const response = await api.tasks.get(params, { retries: 0 });

		return response.data.map(it => {
			return {
				value: it.id,
				label: `${it.variables?.taskCounter} ${it.name}`,
				taskCounter: it.variables?.taskCounter,
				taskName: it.name,
			};
		});
	} catch (e) {
		notifyError("Unable to search Milestones. Please try again.", e);
		console.error("Error loading options: ", e);

		return [];
	}
}
